// Progress bar whose fill grows from 0 to its target width on mount.
//
// This is the design's own `.ccbar { animation: ccGrow .8s ease both }` /
// `@keyframes ccGrow { from { width: 0 } }` from
// DashboardRevampContent.dc.html — every bar in that design (goal tiles, club
// event progress) animates in rather than appearing at full width.

import React, {useEffect} from 'react';
import {StyleProp, ViewStyle} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';

interface AnimatedBarProps {
  /** Target fill, 0–100. */
  percent: number;
  color: string;
  /** Track height; the fill inherits it. */
  height?: number;
  trackColor?: string;
  duration?: number;
  delay?: number;
  style?: StyleProp<ViewStyle>;
}

export default function AnimatedBar({
  percent,
  color,
  height = 6,
  trackColor = 'rgba(38,35,34,0.08)',
  duration = 800,
  delay = 0,
  style,
}: AnimatedBarProps) {
  const target = Math.max(0, Math.min(100, Number(percent) || 0));
  const progress = useSharedValue(0);

  useEffect(() => {
    progress.value = withDelay(
      delay,
      withTiming(target, {duration, easing: Easing.out(Easing.cubic)}),
    );
    // Re-runs when the value changes (e.g. a background refetch), so the bar
    // animates to the new number instead of snapping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const fillStyle = useAnimatedStyle(() => ({
    width: `${progress.value}%`,
  }));

  return (
    <Animated.View
      style={[
        {
          height,
          borderRadius: 99,
          backgroundColor: trackColor,
          overflow: 'hidden',
        },
        style,
      ]}>
      <Animated.View
        style={[
          {height: '100%', borderRadius: 99, backgroundColor: color},
          fillStyle,
        ]}
      />
    </Animated.View>
  );
}
